import type { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import {
  type ComponentByIdSortDependencies,
  SortAttribute,
  getComponentComparator,
} from '@ardoq/common-helpers';
import { type ContextSort, NumericSortOrder } from '@ardoq/data-model';
import type { Locale } from '@ardoq/locale';
import { localeCompareNumericLowercase } from '@ardoq/locale';
import { dateRangeOperations } from '@ardoq/date-range';
import { componentInterface } from '@ardoq/component-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';

const buildComponentAttributesById = (
  componentIds: ArdoqId[]
): Record<ArdoqId, APIComponentAttributes> =>
  componentIds.reduce<Record<ArdoqId, APIComponentAttributes>>((acc, id) => {
    const attrs = componentInterface.get(id);
    if (attrs) acc[id] = attrs;
    return acc;
  }, {});

/**
 * Build a `Map<ArdoqId, number>` such that comparing two ids by their index
 * reproduces a depth-first walk of each workspace's component tree where
 * each level's children are sorted by `_order` (ASC). The sort direction
 * (ASC/DESC) is applied at comparison time, not when building the index —
 * we always walk ASC so the index is stable across re-sorts.
 *
 * Multi-workspace: workspaces are visited in name order (deterministic and
 * mirrors what the navigator menu does when several workspaces are open).
 *
 * Components passed in but not reachable via their workspace tree (orphans,
 * cross-workspace ancestor refs, etc.) are not indexed — `compareComponentsById`
 * falls back to its standard tiebreak for unindexed ids.
 */
const buildTreeOrderIndex = (
  componentIds: ArdoqId[],
  userLocale: Locale
): ReadonlyMap<ArdoqId, number> => {
  const idSet = new Set(componentIds);
  if (idSet.size === 0) return new Map();

  const workspaceIds = new Set<ArdoqId>();
  for (const id of idSet) {
    const workspaceId = componentInterface.getWorkspaceId(id);
    if (workspaceId) workspaceIds.add(workspaceId);
  }

  const orderSort: ContextSort = {
    attr: SortAttribute.ORDER,
    order: NumericSortOrder.ASC,
    orderUnsetLast: false,
  };
  const compareByOrder = getComponentComparator(orderSort, userLocale);

  const sortedWorkspaceIds = [...workspaceIds].sort((a, b) =>
    localeCompareNumericLowercase(
      workspaceInterface.getWorkspaceName(a) ?? '',
      workspaceInterface.getWorkspaceName(b) ?? '',
      userLocale
    )
  );

  const index = new Map<ArdoqId, number>();
  let cursor = 0;

  // Iterative depth-first pre-order walk via an explicit stack. Children are
  // pushed in reverse so the next pop yields the lowest-`_order` sibling,
  // matching what a recursive walk would visit. Avoids JS stack-depth risk
  // and decouples cost from tree depth.
  const stack: ArdoqId[] = [];
  for (const workspaceId of sortedWorkspaceIds) {
    const roots = componentInterface
      .getRootComponents(workspaceId)
      .slice()
      .sort(compareByOrder);
    for (let i = roots.length - 1; i >= 0; i--) {
      stack.push(roots[i]._id);
    }
    while (stack.length > 0) {
      const componentId = stack.pop()!;
      if (idSet.has(componentId)) {
        index.set(componentId, cursor++);
      }
      const children = componentInterface
        .getChildren(componentId)
        .slice()
        .sort(compareByOrder);
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push(children[i]._id);
      }
    }
  }

  return index;
};

type GetComponentByIdSortDependenciesArgs = {
  componentIds: ArdoqId[];
  sort: ContextSort;
  userLocale: Locale;
};

export const getComponentByIdSortDependencies = ({
  componentIds,
  sort,
  userLocale,
}: GetComponentByIdSortDependenciesArgs): ComponentByIdSortDependencies => ({
  componentAttributesById: buildComponentAttributesById(componentIds),
  sort,
  isDateRangeField: dateRangeOperations.isCombinedDateRangeFieldNames(
    sort.attr
  ),
  userLocale,
  treeOrderIndex:
    sort.attr === SortAttribute.ORDER
      ? buildTreeOrderIndex(componentIds, userLocale)
      : undefined,
});
